import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ClipboardList, Loader2, FileCheck2, Copy, ExternalLink, Check, X,
    ChevronDown, ChevronUp, Building2, Mail, Phone, MapPin, User, Trash2, KeyRound,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Badge, Panel } from "./BuyerPortal";

const STATUS_TABS = [
    { k: "pending",  l: "Pending Review",  tone: "gold" },
    { k: "approved", l: "Approved",        tone: "green" },
    { k: "rejected", l: "Rejected",        tone: "red" },
];

const DOC_LABELS = {
    passport_photo: "Passport Photo",
    pan_card: "PAN Card",
    aadhaar_card: "Aadhaar",
    gst_certificate: "GST Cert.",
    rera_certificate: "RERA Cert.",
    cancelled_cheque: "Cancelled Cheque",
    company_registration: "Company Reg.",
    visiting_card: "Visiting Card",
    selfie: "Selfie (KYCP)",
};

export default function CPApplicationsPanel() {
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [ptype, setPtype] = useState("");

    const load = async (s = status) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/cp-applications?status=${s}${ptype ? `&partner_type=${ptype}` : ""}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [status, ptype]); // eslint-disable-line react-hooks/exhaustive-deps

    const askPassword = (msg) => {
        const pw = window.prompt(msg, "");
        if (pw === null) return null;
        if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return null; }
        return pw;
    };

    const approve = async (app) => {
        const body = {};
        if (!app.email) {
            const email = window.prompt("This applicant registered without an email.\n\nEnter their email to send a Set-Password link, or leave blank to set a password yourself:", "");
            if (email === null) return;
            if (email.trim()) body.email = email.trim();
            else {
                const pw = askPassword(`Set a password for ${app.proprietor_name} (min 8 characters). They will log in with mobile ${app.mobile}:`);
                if (!pw) return;
                body.password = pw;
            }
        }
        body.notes = window.prompt("Optional notes for internal record:", "") || "";
        try {
            const { data } = await api.post(`/admin/cp-applications/${app.id}/approve`, body);
            if (data.set_password_path) {
                window.prompt("Approved! Share this Set-Password link with the partner (copy from below):", `${window.location.origin}${data.set_password_path}`);
            } else {
                window.prompt("Approved! Share these login details with the partner:", `Mobile: ${data.login_id} · Password: ${body.password}`);
            }
            toast.success("Application approved. Partner user created.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reject = async (id) => {
        const reason = window.prompt("Reason for rejection (visible to the applicant):");
        if (!reason) return;
        try {
            await api.post(`/admin/cp-applications/${id}/reject`, { reason });
            toast.success("Application rejected — applicant has been emailed.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (id, company) => {
        const removeUser = window.confirm(
            `Delete the application from "${company}"?\n\n` +
            `Click OK to also delete the linked partner user account (if any).\n` +
            `Click Cancel to keep the user but remove only the application.`
        );
        // Use a second confirm to give the admin a chance to fully cancel
        if (!window.confirm(`Are you absolutely sure? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/cp-applications/${id}?delete_user=${removeUser}`);
            toast.success(removeUser ? "Application and user deleted." : "Application deleted.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const resetPw = async (app) => {
        try {
            if (!app.email) {
                const pw = askPassword(`Set a new password for ${app.proprietor_name} (min 8 characters):`);
                if (!pw) return;
                const { data } = await api.post(`/admin/cp-applications/${app.id}/reset-password`, { password: pw });
                window.prompt("Password updated. Share these login details with the partner:", `Mobile: ${data.login_id} · Password: ${pw}`);
                toast.success("Password updated.");
                return;
            }
            if (!window.confirm(`Send a password-reset link to ${app.email}?`)) return;
            const { data } = await api.post(`/admin/cp-applications/${app.id}/reset-password`);
            window.prompt("Reset link emailed to the partner. You can also copy the link below to share manually:", `${window.location.origin}${data.set_password_path}`);
            toast.success("Password reset link sent by email.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Panel title="Channel Partner Applications" testid="admin-cp-applications">
            <div className="flex items-center gap-2 mb-6 flex-wrap">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setStatus(t.k)}
                        data-testid={`cpa-tab-${t.k}`}
                        className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${status === t.k ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"}`}
                    >{t.l}</button>
                ))}
                <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="cpa-type-filter" className="ml-2 border border-black/15 px-2 py-2 text-[11px] font-bold bg-white">
                    <option value="">All types</option><option value="FREE">Channel Partners</option><option value="PAID">Premium Channel Partners</option>
                </select>
                <button onClick={() => load()} className="text-[11px] uppercase tracking-[0.18em] font-bold text-black/50 hover:text-black ml-auto">Refresh</button>
            </div>

            {loading ? (
                <div className="py-14 flex items-center justify-center text-black/40"><Loader2 className="animate-spin mr-2"/> Loading…</div>
            ) : items.length === 0 ? (
                <div className="py-14 text-center text-black/40 text-sm">No {status} applications.</div>
            ) : (
                <div className="space-y-3">
                    {items.map((a) => (
                        <ApplicationRow
                            key={a.id} app={a}
                            expanded={expanded === a.id}
                            onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
                            onApprove={() => approve(a)}
                            onReject={() => reject(a.id)}
                            onDelete={() => del(a.id, a.company_name)}
                            onResetPw={() => resetPw(a)}
                        />
                    ))}
                </div>
            )}
        </Panel>
    );
}


function ApplicationRow({ app, expanded, onToggle, onApprove, onReject, onDelete, onResetPw }) {
    const docs = app.documents || {};
    const docCount = Object.keys(docs).length;
    return (
        <div className="bg-white border border-black/10" data-testid={`cpa-row-${app.id.slice(0, 8)}`}>
            <div className="flex items-center gap-4 px-4 py-4 flex-wrap md:flex-nowrap">
                <div className="w-11 h-11 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center shrink-0">
                    <Building2 size={17} className="text-[var(--ebg-gold)]"/>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold truncate flex items-center gap-2">{app.company_name || app.proprietor_name}<span data-testid={`cpa-type-${app.id.slice(0, 8)}`} className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${app.partner_type === "PAID" ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)] text-black" : "border-emerald-600 text-emerald-700"}`}>{app.partner_type === "PAID" ? "Premium" : "Channel Partner"}</span></div>
                    <div className="text-[12px] text-black/55 truncate">{app.proprietor_name} · {app.email || "No email"} · {app.mobile}</div>
                </div>
                <div className="text-[11px] text-black/55 hidden md:block">{[app.city, app.state].filter(Boolean).join(", ")}</div>
                <Badge tone={app.status === "approved" ? "green" : app.status === "rejected" ? "red" : "gold"}>{app.status}</Badge>
                {app.partner_type !== "FREE" && <div className="text-[11px] text-black/55">{docCount}/9 docs</div>}
                <div className="flex items-center gap-2 ml-auto">
                    {app.status === "pending" && (
                        <>
                            <button onClick={onApprove} data-testid={`cpa-approve-${app.id.slice(0, 8)}`} className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-bold hover:bg-emerald-700"><Check size={12}/> Approve</button>
                            <button onClick={onReject} data-testid={`cpa-reject-${app.id.slice(0, 8)}`} className="inline-flex items-center gap-1 border border-red-500 text-red-600 px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-bold hover:bg-red-500 hover:text-white"><X size={12}/> Reject</button>
                        </>
                    )}
                    {app.status === "approved" && (
                        <button onClick={onResetPw} data-testid={`cpa-reset-${app.id.slice(0, 8)}`} title="Send a new Set-Password link" className="inline-flex items-center gap-1 border border-black/20 text-black px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-bold hover:bg-black hover:text-white">
                            <KeyRound size={12}/> Reset PW
                        </button>
                    )}
                    <button onClick={onDelete} data-testid={`cpa-delete-${app.id.slice(0, 8)}`} title="Delete this application" className="inline-flex items-center justify-center w-9 h-9 border border-black/15 text-black/50 hover:bg-red-500 hover:text-white hover:border-red-500">
                        <Trash2 size={13}/>
                    </button>
                    <button onClick={onToggle} className="text-black/40 hover:text-black p-1" data-testid={`cpa-expand-${app.id.slice(0, 8)}`}>
                        {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="border-t border-black/8 px-4 py-5 grid md:grid-cols-2 gap-6 bg-black/[0.015] text-[13px]">
                    <div>
                        <SectionH>Contact</SectionH>
                        <Kv l="Company" v={app.company_name}/>
                        <Kv l="Proprietor" v={app.proprietor_name}/>
                        <Kv l="Email" v={app.email || "—"}/>
                        <Kv l="Mobile" v={app.mobile + (app.alternate_mobile ? " · " + app.alternate_mobile : "")}/>
                        <Kv l="Address" v={`${app.office_address}, ${app.city}, ${app.state || ""}${app.pin_code ? " - " + app.pin_code : ""}`}/>
                    </div>
                    <div>
                        <SectionH>Business</SectionH>
                        <Kv l="Type" v={app.business_type + (app.business_type_other ? " (" + app.business_type_other + ")" : "")}/>
                        <Kv l="PAN" v={app.pan_number}/>
                        <Kv l="RERA" v={app.rera_registration_number || "—"}/>
                        <Kv l="GST" v={app.gst_number || "—"}/>
                        <Kv l="Experience" v={app.experience_bucket}/>
                        <Kv l="Areas" v={(app.areas_of_operation || []).join(", ")}/>
                        <Kv l="Categories" v={(app.property_categories || []).join(", ")}/>
                        <Kv l="Services" v={(app.services_offered || []).join(", ")}/>
                    </div>
                    <div>
                        <SectionH>Banking</SectionH>
                        {app.banking ? (
                            <>
                                <Kv l="Holder" v={app.banking.account_holder_name}/>
                                <Kv l="Bank" v={app.banking.bank_name}/>
                                <Kv l="A/c No." v={app.banking.account_number}/>
                                <Kv l="IFSC" v={app.banking.ifsc_code}/>
                                <Kv l="UPI" v={app.banking.upi_id || "—"}/>
                            </>
                        ) : <span className="text-black/40">Not provided</span>}
                    </div>
                    <div>
                        <SectionH>Documents ({docCount}/9)</SectionH>
                        <div className="grid grid-cols-2 gap-1.5">
                            {Object.entries(DOC_LABELS).map(([slot, label]) => {
                                const d = docs[slot];
                                if (!d) return (
                                    <div key={slot} className="text-[11px] text-black/35 py-1.5 px-2 border border-dashed border-black/10">{label} — missing</div>
                                );
                                return (
                                    <a key={slot} href={`${api.defaults.baseURL}/admin/cp-applications/files/${d.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] text-black hover:text-[var(--ebg-gold)] py-1.5 px-2 border border-black/10 hover:border-[var(--ebg-gold)]">
                                        <FileCheck2 size={11} className="text-[var(--ebg-gold)]"/> {label} <ExternalLink size={10} className="ml-auto opacity-40"/>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <SectionH>Declaration</SectionH>
                        <div className="text-[12px] text-black/70">
                            Signed by <b>{app.signature_name}</b> on <b>{app.signed_date}</b> at <b>{app.signed_place}</b>.
                            Declaration accepted: {app.declaration_accepted ? "✓" : "✗"} · Terms accepted: {app.terms_accepted ? "✓" : "✗"}
                        </div>
                        {app.review_notes && (
                            <div className="mt-3 p-3 bg-white border border-black/8 text-[12px]">
                                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/50 mb-1">Review Notes</div>
                                {app.review_notes}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionH({ children }) { return <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mb-3">{children}</div>; }
function Kv({ l, v }) { return <div className="flex gap-3 py-1"><div className="text-black/45 w-20 shrink-0 text-[11px] uppercase tracking-wide">{l}</div><div className="text-black">{v || "—"}</div></div>; }
